﻿import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "@foundation/contextapi/UserContext";
import { usePluginNavigation } from "../plugins/hooks";
import { useAdminAuth } from "@foundation/hooks/useAdminAuth";
import { supabase } from "@foundation/utils/supabaseClient";
import { SendInviteDialog } from "@foundation/components/invitation/SendInviteDialog";
import {
  UserPlus,
  User,
  Settings,
  LogOut,
  ChevronDown,
  LayoutDashboard,
  Shield,
} from "lucide-react";
// import { ThemeToggle } from "@foundation/components/theme-toggle"; // TODO: re-enable dark mode later

const Header = () => {
  const { user } = useUser();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Fetch avatar and display name
  useEffect(() => {
    if (!user?.id) { setAvatarUrl(null); setDisplayName(""); return; }
    const authAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture || null;
    const authName = user.user_metadata?.full_name || user.user_metadata?.name || "";
    if (authAvatar) setAvatarUrl(authAvatar);
    if (authName) setDisplayName(authName);

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from("base_profiles")
          .select("avatar, full_name, first_name, last_name")
          .eq("id", user.id)
          .single();
        if (!error && data) {
          if (data.avatar) setAvatarUrl(data.avatar);
          const name = data.full_name || [data.first_name, data.last_name].filter(Boolean).join(" ");
          if (name) setDisplayName(name);
        }
      } catch { /* fallback to auth metadata */ }
    };
    fetchProfile();
  }, [user?.id, user?.user_metadata?.avatar_url, user?.user_metadata?.picture, user?.user_metadata?.full_name, user?.user_metadata?.name]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  const { navigation: pluginNavigation } = usePluginNavigation();
  const { isAdmin } = useAdminAuth();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const isActive = (path: string) => {
    if (path === "/settings") return location.pathname.startsWith("/settings") || location.pathname.startsWith("/organizations") || location.pathname.startsWith("/teams") || location.pathname.startsWith("/subscription");
    if (path === "/admin") return location.pathname.startsWith("/admin");
    if (path === "/org-admin") return location.pathname.startsWith("/org-admin");
    if (path === "/revisr") return location.pathname.startsWith("/revisr");
    if (path === "/pseo") return location.pathname.startsWith("/pseo");
    if (path === "/copilot-chat") return location.pathname.startsWith("/copilot-chat");
    if (path === "/formengine") return location.pathname.startsWith("/formengine");
    return location.pathname === path;
  };

  const staticNavigation = [{ name: "Settings", route: "/settings", order: 10 }];
  if (isAdmin) staticNavigation.push({ name: "Admin", route: "/admin", order: 12 });
  const allNavigation = [...staticNavigation, ...pluginNavigation].sort((a, b) => (a.order || 999) - (b.order || 999));

  const initial = (displayName || user?.email || "U").charAt(0).toUpperCase();

  return (
    <>
      <header className="app-header sticky top-0 z-40">
        <div className="max-w-full px-5 h-14 flex items-center justify-between gap-6">

          {/* Left: logo + nav */}
          <div className="flex items-center gap-8 h-full">
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
              <img src="/favicon.png" alt="Revisr" className="h-6 w-6 object-contain" />
              <span className="text-[15px] font-bold tracking-tight text-foreground" >Revisr</span>
            </Link>

            {user && (
              <nav className="hidden md:flex items-center gap-1 h-full">
                {allNavigation.map((item) => (
                  <Link
                    key={item.route}
                    to={item.route}
                    className={isActive(item.route) ? "header-link-active" : "header-link"}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            )}
          </div>

          {/* Right: actions */}
          {user ? (
            <div className="hidden md:flex items-center gap-2">

              {/* Invite button */}
              <button
                onClick={() => setInviteOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150"
                style={{ color: "hsl(var(--muted-foreground))" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "hsl(var(--accent))"; (e.currentTarget as HTMLElement).style.color = "hsl(var(--accent-foreground))"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "hsl(var(--muted-foreground))"; }}
              >
                <UserPlus className="w-3.5 h-3.5" />
                Invite
              </button>

              {/* <ThemeToggle /> TODO: re-enable dark mode later */}
              {/* Divider */}
              <div className="w-px h-5 bg-border mx-1" />

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl transition-all duration-150"
                  style={{ background: userMenuOpen ? "hsl(var(--accent))" : "transparent" }}
                  onMouseEnter={(e) => { if (!userMenuOpen) (e.currentTarget as HTMLElement).style.background = "hsl(var(--accent))"; }}
                  onMouseLeave={(e) => { if (!userMenuOpen) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  {/* Avatar */}
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="w-7 h-7 rounded-lg object-cover"
                      style={{ boxShadow: "0 0 0 2px rgba(19,35,33,0.12)" }}
                      referrerPolicy="no-referrer"
                      onError={() => setAvatarUrl(null)}
                    />
                  ) : (
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
                      style={{ background: "#132321" }}
                    >
                      {initial}
                    </div>
                  )}
                  <span className="text-[13px] font-medium text-foreground max-w-[110px] truncate leading-none">
                    {displayName || user.email?.split("@")[0]}
                  </span>
                  <ChevronDown
                    className="w-3.5 h-3.5 text-muted-foreground transition-transform duration-200"
                    style={{ transform: userMenuOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>

                {/* —— Rich dropdown —— */}
                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 z-50 overflow-hidden"
                    style={{
                      width: "260px",
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "16px",
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.06), 0 16px 48px -8px rgba(19,35,33,0.16), 0 0 0 1px rgba(255,255,255,0.8)",
                    }}
                  >
                    {/* Dark header panel */}
                    <div
                      className="px-4 py-4 relative overflow-hidden"
                      style={{ background: "#132321" }}
                    >
                      {/* Subtle dot grid texture */}
                      <div
                        className="absolute inset-0 pointer-events-none opacity-[0.04]"
                        style={{
                          backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
                          backgroundSize: "20px 20px",
                        }}
                      />
                      {/* Green top accent */}
                      <div
                        className="absolute top-0 left-0 right-0 h-[2px]"
                        style={{ background: "linear-gradient(90deg, #33b864 0%, rgba(51,184,100,0.2) 100%)" }}
                      />

                      <div className="flex items-center gap-3 relative">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt=""
                            className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                            style={{ boxShadow: "0 0 0 2px rgba(51,184,100,0.3)" }}
                            referrerPolicy="no-referrer"
                            onError={() => setAvatarUrl(null)}
                          />
                        ) : (
                          <div
                            className="w-11 h-11 rounded-xl flex items-center justify-center text-base font-bold flex-shrink-0"
                            style={{
                              background: "rgba(51,184,100,0.15)",
                              border: "1px solid rgba(51,184,100,0.25)",
                              color: "#33b864",
                            }}
                          >
                            {initial}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-white leading-snug truncate">
                            {displayName || user.email?.split("@")[0]}
                          </p>
                          <p className="text-[11px] mt-0.5 truncate" style={{ color: "rgba(255,255,255,0.45)" }}>
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="p-1.5 space-y-0.5">
                      <DropdownItem
                        to="/profile"
                        icon={<User className="w-3.5 h-3.5" />}
                        label="Profile"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <DropdownItem
                        to="/revisr/dashboard"
                        icon={<LayoutDashboard className="w-3.5 h-3.5" />}
                        label="Dashboard"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <DropdownItem
                        to="/settings"
                        icon={<Settings className="w-3.5 h-3.5" />}
                        label="Settings"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      {isAdmin && (
                        <DropdownItem
                          to="/admin"
                          icon={<Shield className="w-3.5 h-3.5" />}
                          label="Admin Portal"
                          onClick={() => setUserMenuOpen(false)}
                        />
                      )}
                    </div>

                    {/* Divider */}
                    <div className="mx-3 h-px" style={{ background: "hsl(var(--border))" }} />

                    {/* Logout */}
                    <div className="p-1.5">
                      <button
                        onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150"
                        style={{ color: "#ef4444" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.06)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="hidden md:block">
              <Link
                to="/login"
                className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ background: "#132321" }}
              >
                Login
              </Link>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background">
            <div className="px-4 py-3 space-y-1">
              {user ? (
                <>
                  {/* Mobile user info */}
                  <div className="flex items-center gap-3 px-3 py-3 mb-2 rounded-xl" style={{ background: "#132321" }}>
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="w-9 h-9 rounded-lg object-cover" referrerPolicy="no-referrer" onError={() => setAvatarUrl(null)} />
                    ) : (
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold" style={{ background: "rgba(51,184,100,0.2)", color: "#33b864" }}>{initial}</div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{displayName || user.email?.split("@")[0]}</p>
                      <p className="text-[11px] truncate" style={{ color: "rgba(255,255,255,0.45)" }}>{user.email}</p>
                    </div>
                  </div>
                  {allNavigation.map((item) => (
                    <Link
                      key={`mobile-${item.route}`}
                      to={item.route}
                      className={`flex items-center gap-2 py-3 px-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${isActive(item.route) ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                  <div className="h-px bg-border my-2" />
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 w-full py-2 px-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </>
              ) : (
                <Link to="/login" className="block py-2 px-3 rounded-lg text-sm font-medium text-white" style={{ background: "#132321" }} onClick={() => setMobileMenuOpen(false)}>
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <SendInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </>
  );
};

// —— Shared dropdown item ———————————————————————————

type DropdownItemProps = {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
};

const DropdownItem = ({ to, icon, label, onClick }: DropdownItemProps) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150"
    style={{ color: "hsl(var(--foreground))" }}
    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "hsl(var(--accent))"; (e.currentTarget as HTMLElement).style.color = "hsl(var(--accent-foreground))"; }}
    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "hsl(var(--foreground))"; }}
  >
    <span style={{ color: "hsl(var(--muted-foreground))" }}>{icon}</span>
    {label}
  </Link>
);

export default Header;

